import { useState, useMemo } from 'react';
import { FiSearch } from 'react-icons/fi';
import { formatCurrency } from './collateralUtils';

export interface OperationItem {
    id: string;
    operationCode: string;
    assignorName: string;
    status: string;
    totalAmount: number;
    totalReceivables: number;
    totalDocuments?: number;
}

interface Props {
    operations: any[];
    isLoading: boolean;
    selected: any;
    onSelect: (op: any) => void;
    showStatusFilter?: boolean;
}

const OperationSelectList = ({ operations, isLoading, selected, onSelect, showStatusFilter = false }: Props) => {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const filtered = useMemo(() => {
        let ops = operations;
        if (search) {
            const s = search.toLowerCase();
            ops = ops.filter(o => o.operationCode?.toLowerCase().includes(s) || o.assignorName?.toLowerCase().includes(s));
        }
        if (statusFilter) {
            ops = ops.filter(o => o.status === statusFilter);
        }
        return ops;
    }, [operations, search, statusFilter]);

    return (
        <>
            <div className={`flex items-center gap-2 mb-3`}>
                <div className="relative flex-1">
                    <FiSearch className="absolute left-3 top-2.5 text-gray-400" size={16} />
                    <input type="text" placeholder="Buscar por código ou cedente..." value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
                </div>
                {showStatusFilter && (
                    <div className="flex rounded-md border border-gray-300 dark:border-slate-600 overflow-hidden text-xs">
                        {([['', 'Todos'], ['closed', 'Fechado'], ['completed', 'Aberto']] as const).map(([val, label]) => (
                            <button key={val} onClick={() => setStatusFilter(val)}
                                className={`px-3 py-2 transition-colors ${statusFilter === val
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-auto space-y-2" style={{ maxHeight: 'calc(100vh - 140px)' }}>
                {filtered.length === 0 && !isLoading ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nenhuma operação disponível</div>
                ) : filtered.map(op => (
                    <div key={op.id} onClick={() => onSelect(op)}
                        className={`p-3 rounded-lg border transition-colors cursor-pointer ${selected?.id === op.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                            : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600'}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm dark:text-white">{op.operationCode}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${op.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {op.status === 'closed' ? 'Fechado' : 'Aberto'}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{op.assignorName}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm font-medium dark:text-white">{formatCurrency(op.totalAmount || 0)}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{op.totalReceivables || 0} títulos{op.totalDocuments != null ? ` · ${op.totalDocuments} docs` : ''}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default OperationSelectList;
